// src/pages/AuthCallback.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleAuthCallback } from '../services/authService';
import LoadingState from '../components/common/LoadingState';
import { debugAuthCallback } from '../services/authDebugUtils';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(true);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Extract code from URL
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        
        // Store debug info
        const debug = debugAuthCallback(code, params);
        setDebugInfo(debug);
        console.log('Auth callback debug info:', debug);
        
        // Handle error
        const errorCode = params.get('error');
        if (errorCode) {
          setError(`Bungie Authentication Error: ${errorCode}`);
          setProcessing(false);
          return;
        }
        
        if (!code) {
          setError('No authorization code received from Bungie');
          setProcessing(false);
          return;
        }
        
        console.log('Received authorization code:', code);
        
        // Process auth code
        const success = await handleAuthCallback(code);
        
        if (success) {
          // Get the stored auth state from localStorage to verify it was saved properly
          const storedAuth = localStorage.getItem('destiny_auth');
          if (storedAuth) {
            const authData = JSON.parse(storedAuth);
            console.log('Auth successful - stored data:', {
              isAuthenticated: authData.isAuthenticated,
              membershipId: authData.membershipId,
              destinyMembershipId: authData.destinyMembershipId,
              membershipType: authData.membershipType,
              displayName: authData.displayName,
              tokenExpires: authData.expires ? new Date(authData.expires).toISOString() : 'none'
            });
            
            // Double check if we have all the necessary data
            if (!authData.destinyMembershipId || !authData.membershipType) {
              console.warn('Missing required Destiny membership information!');
            }
          } else {
            console.warn('Auth success but no data stored in localStorage!');
          }
          
          // Redirect to home or saved return path on success
          const returnPath = sessionStorage.getItem('returnPath') || '/';
          sessionStorage.removeItem('returnPath');
          
          // Trigger auth state changed event so navbar updates
          window.dispatchEvent(new Event('auth-state-changed'));
          
          navigate(returnPath);
        } else {
          setError('Error authenticating with Bungie');
          setProcessing(false);
        }
      } catch (err: any) {
        console.error('Error during auth callback:', err);
        setError(`An unexpected error occurred: ${err.message || 'Unknown error'}`);
        setProcessing(false);
      }
    };
    
    processCallback();
  }, [location, navigate]);

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">Authentication Error</h2>
        <p className="text-gray-300 mb-4">{error}</p>
        
        {/* Show debug info in development mode */}
        {process.env.NODE_ENV === 'development' && debugInfo && (
          <div className="mt-4 border-t border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-400 mb-2">Debug Information</h3>
            <pre className="bg-gray-900 p-2 rounded text-xs text-gray-300 overflow-auto max-h-60">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
        
        <button
          onClick={() => navigate('/login')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-destiny-blue hover:bg-opacity-90"
        >
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center min-h-[50vh]">
      <LoadingState message="Authenticating with Bungie..." />
      {processing && (
        <p className="text-gray-400 mt-4 text-sm">
          Please wait while we process your Bungie login credentials...
        </p>
      )}
    </div>
  );
};

export default AuthCallback;